import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';


export default function Dashboard() {
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('active');
  const [department, setDepartment] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    api('/hcm/departments').then(r => r.ok ? r.json() : []).then(setDepartments).catch(() => {});
  }, []);

  const loadEmployees = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('q', searchTerm);
    if (status) params.set('status', status);
    if (department) params.set('department', department);
    if (employmentType) params.set('employmentType', employmentType);
    const qs = params.toString();
    api(`/hcm/employees${qs ? `?${qs}` : ''}`).then(r => r.json()).then(setEmployees).catch(() => {});
  };

  useEffect(() => {
    const timeout = setTimeout(loadEmployees, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, status, department, employmentType]);

  return (
    <Layout>
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 md:p-6 border-b">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Employees</h1>
            <Link to="/hcm/employees/new" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
              Add Employee
            </Link>
          </div>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 mb-3"
          />
          <div className="flex flex-wrap gap-3">
            <select value={status} onChange={e => setStatus(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600">
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="on_leave">On Leave</option>
              <option value="terminated">Terminated</option>
            </select>
            <select value={department} onChange={e => setDepartment(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600">
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
            <select value={employmentType} onChange={e => setEmploymentType(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600">
              <option value="">All Types</option>
              <option value="full_time">Full-Time</option>
              <option value="part_time">Part-Time</option>
              <option value="contractor">Contractor</option>
              <option value="intern">Intern</option>
            </select>
            {(status || department || employmentType) && (
              <button onClick={() => { setStatus(''); setDepartment(''); setEmploymentType(''); }} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700">Clear filters</button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Job Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Department</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Linked</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {employees.map((e) => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{e.employeeId || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                    <Link to={`/hcm/employees/${e.id}`} className="text-indigo-600 hover:text-indigo-800">
                      {[e.title, e.firstName, e.displayMiddleName ? e.middleName : null, e.lastName, e.suffix].filter(Boolean).join(' ')}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600 hidden md:table-cell">{e.jobTitle || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600 hidden md:table-cell">{e.department || '—'}</td>
                  <td className="px-6 py-4 text-center">
                    {e.status ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${e.status === 'active' ? 'bg-green-100 text-green-800' : e.status === 'terminated' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {e.status.charAt(0).toUpperCase() + e.status.slice(1)}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-6 py-4 text-center hidden lg:table-cell" title={e.userId ? 'Account linked' : 'Not linked'}>
                    {e.userId
                      ? <svg className="w-5 h-5 text-green-600 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                      : <svg className="w-5 h-5 text-gray-300 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {employees.length === 0 && (
            <div className="text-center py-12 text-gray-500">No employees found.</div>
          )}
        </div>
      </div>
    </Layout>
  );
}
